/** Tiny path helpers shared by the inspect Worker (must not import `api.ts`). */

/** Hosted revision id from `:revId` or `?rev=` — no `/`, `.`, or `..`. */
const REVISION_ID = /^(?!\.\.?$)[A-Za-z0-9._-]+$/;
const FILES_REV_PATH = /\/(?:files|hardware)\/revisions\/([^/?#]+)/;

export function isRevisionId(rev: string): boolean {
  return REVISION_ID.test(rev);
}

export function basenamePath(path: string): string {
  const i = path.replaceAll("\\", "/").lastIndexOf("/");
  return i >= 0 ? path.slice(i + 1) : path;
}

export function isKicadSch(path: string): boolean {
  return path.toLowerCase().endsWith(".kicad_sch");
}

export function isKicadPcb(path: string): boolean {
  return path.toLowerCase().endsWith(".kicad_pcb");
}

export function isKicadPro(path: string): boolean {
  return path.toLowerCase().endsWith(".kicad_pro");
}

/** Same-origin Worker URL for one hosted revision file (session cookie). */
export function fileUrl(org: string, proj: string, revId: string, path: string): string {
  const segs = path.replaceAll("\\", "/").replace(/^\/+/, "").split("/").filter(Boolean);
  return `/api/o/${encodeURIComponent(org)}/p/${encodeURIComponent(proj)}/hardware/revisions/${encodeURIComponent(revId)}/files/${segs.map(encodeURIComponent).join("/")}`;
}

function decodeRevSegmentVerbatim(raw: string): string | null {
  const token = raw.trim();
  if (!token) return null;
  try {
    const decoded = decodeURIComponent(token).trim();
    return decoded || null;
  } catch {
    return token;
  }
}

function decodeRevSegment(raw: string): string | null {
  const verbatim = decodeRevSegmentVerbatim(raw);
  if (!verbatim) return null;
  return isRevisionId(verbatim) ? verbatim : null;
}

/** `/files/revisions/:revId` or legacy `/hardware/revisions/:revId`. */
export function revIdFromFilesPath(pathname: string | null | undefined): string | null {
  const path = (pathname ?? "").replaceAll("\\", "/");
  const match = FILES_REV_PATH.exec(path);
  return match?.[1] ? decodeRevSegment(match[1]) : null;
}

/** `?rev=` on `/files` (or any files search) when the path has no `:revId`. */
export function revIdFromSearch(search: string | null | undefined): string | null {
  const raw = (search ?? "").trim();
  if (!raw) return null;
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const rev = params.get("rev");
  return rev ? decodeRevSegment(rev) : null;
}

/**
 * URL-known rev: route param, then `/files/revisions/:revId`, then `?rev=`.
 * Listing-picked rev is not URL-known — F1 then-edge owns that.
 */
export function knownRevisionIdFromRoute(opts: {
  routeRevId?: string | null;
  pathname?: string | null;
  search?: string | null;
}): string | null {
  const fromParam = typeof opts.routeRevId === "string" ? decodeRevSegment(opts.routeRevId) : null;
  if (fromParam) return fromParam;
  return revIdFromFilesPath(opts.pathname) ?? revIdFromSearch(opts.search);
}

/**
 * URL rev token for Think `pcbx_view_rev` — no client-side validity filter.
 * Invalid / unknown ids reach the workbench D1 lookup and return 404 (no HEAD fallback).
 */
export function verbatimRevisionIdFromRoute(opts: {
  routeRevId?: string | null;
  pathname?: string | null;
  search?: string | null;
}): string | null {
  const fromParam = typeof opts.routeRevId === "string" ? decodeRevSegmentVerbatim(opts.routeRevId) : null;
  if (fromParam) return fromParam;
  const path = (opts.pathname ?? "").replaceAll("\\", "/");
  const match = FILES_REV_PATH.exec(path);
  if (match?.[1]) {
    const fromPath = decodeRevSegmentVerbatim(match[1]);
    if (fromPath) return fromPath;
  }
  const raw = (opts.search ?? "").trim();
  if (raw) {
    const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
    const rev = params.get("rev");
    if (rev != null && rev !== "") {
      return decodeRevSegmentVerbatim(rev);
    }
  }
  return null;
}
